/*eslint max-lines-per-function: ["error", 275]*/
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import moment from 'moment';
import { Alert } from 'react-bootstrap';

import {
  CreateUpdateContractDto,
  CreateUpdateContractEstimateItemDto,
  GetContractExtendedDto,
  GetEstimateItemContractDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { convert } from '../../../common/pokCore/contexts/ContractEstimateItemContext';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import mathUtils from '../../../utils/mathUtils';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { ContractStatusEnum } from '../../../common/pokCore/validation/enums';

import { ContractTabsEnum } from './ContractTabsEnum';
import { ContractEstimateItemGrid } from './ContractEstimateItemGrid';
import {
  checkAmountInfo,
  getContractItem,
  getNotAssignedAmount,
  getSumData,
  handleAmountChangeWithoutChangeState,
  SumData,
} from './contractEstimateItemGridUtils';
import { ContractEstimateItemControls } from './ContractEstimateItemControls';
import {
  ContractEstimateItemFilters,
  FiltersParams,
} from './ContractEstimateItemFilters';

interface ContractEstimateItemTabProps {
  contract: CreateUpdateContractDto;
  originalContract?: CreateUpdateContractDto;
  extendedContract?: GetContractExtendedDto;
  setOriginalContract?: Dispatch<
    SetStateAction<CreateUpdateContractDto | undefined>
  >;
  contractId?: string;
  setContract?: Dispatch<SetStateAction<CreateUpdateContractDto | undefined>>;
  readonly: boolean;
  setNotSavedTab: Dispatch<SetStateAction<ContractTabsEnum | undefined>>;
  notSavedTab?: ContractTabsEnum;
  handleRefresh: () => void;
  setGeneralTabErrors: Dispatch<SetStateAction<string[]>>;
}

export const ContractEstimateItemTab: React.FC<
  ContractEstimateItemTabProps
> = props => {
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [availableEstimateItems, setAvailableEstimateItems] =
    useState<GetEstimateItemContractDto[]>();
  const [allAvailableEstimateItems, setAllAvailableEstimateItems] =
    useState<GetEstimateItemContractDto[]>();
  const [filters, setFilters] = useState<FiltersParams>({
    showSettled: false,
    dateFrom: null,
    dateTo: null,
  });

  const [contractEstimateItems, setContractEstimateItems] = useState<
    CreateUpdateContractEstimateItemDto[]
  >([]);
  const [sumData, setSumData] = useState<SumData>({
    positionsTotalSum: 0,
    positionsNotAssignedSum: 0,
    contractSettledAmount: 0,
    contractAmount: 0,
    contractUnsettledAmount: 0,
  });

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('projectLng');

  useEffect(() => {
    const ces =
      props.extendedContract?.contractEstimateItems?.map(o => convert(o)) || [];
    setContractEstimateItems(ces);
  }, [props.extendedContract]);

  useEffect(() => {
    setSaveErrors(
      checkAmountInfo(
        t,
        tk,
        props.contract?.amount || 0,
        contractEstimateItems.reduce(
          (acc, o) => mathUtils.add(acc, o.amount),
          0,
        ),
      ),
    );
  }, [contractEstimateItems, props.contract?.amount, t, tk]);

  useEffect(() => {
    if (props.contract?.projectId && props.contract?.clientId) {
      pok.estimateItems
        .findByProjectAndClient(
          props.contract?.projectId,
          props.contract?.clientId,
          true,
          props.contractId,
        )
        .then(res => {
          setAllAvailableEstimateItems(res);
        })
        .catch(errorResponse => {
          notifications.caughtError(errorResponse);
        });
    } else {
      setAllAvailableEstimateItems([]);
    }
  }, [
    pok.estimateItems,
    notifications,
    props.contract?.clientId,
    props.contract?.projectId,
    props.contractId,
    contractEstimateItems,
    props.extendedContract,
  ]);

  useEffect(() => {
    let ev: typeof allAvailableEstimateItems;
    if (filters.showSettled) {
      ev = allAvailableEstimateItems;
    } else {
      ev = allAvailableEstimateItems?.filter(
        o =>
          o.contractEstimateItems.some(
            p => p.contract?.id === props.contractId,
          ) ||
          contractEstimateItems.some(p => p.estimateItemId === o.id) ||
          mathUtils.round(
            mathUtils.subtract(
              o.purchaseNetTotal,
              o.contractEstimateItems
                .filter(p => p.active)
                .reduce((acc, o) => mathUtils.add(acc, o.amount), 0),
            ),
          ) > 0,
      );
    }

    if (filters.dateFrom && filters.dateTo) {
      ev = ev?.filter(o =>
        moment(o.estimateByMonth?.date).isBetween(
          moment(filters.dateFrom),
          moment(filters.dateTo),
        ),
      );
    }
    if (props.contract?.tv) {
      ev = ev?.filter(o => o?.estimateByMonth?.profile?.tvBuying);
    } else {
      ev = ev?.filter(o => !o?.estimateByMonth?.profile?.tv);
    }

    setSumData(
      getSumData(
        contractEstimateItems,
        allAvailableEstimateItems || [],
        ev || [],
        props.extendedContract,
      ),
    );
    setAvailableEstimateItems(ev);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAvailableEstimateItems, filters, props.contractId]);

  const handleAmountChange = (
    estimateItemId: string,
    item: CreateUpdateContractEstimateItemDto | undefined,
    value: string | null | undefined,
  ) => {
    const updatedItems = handleAmountChangeWithoutChangeState(
      estimateItemId,
      item,
      value,
      contractEstimateItems,
      props.contractId,
    );
    checkAmountInfo(t, tk, props.contract?.amount, value || '0');

    setContractEstimateItems(updatedItems);
    updateSetNotSavedTab(updatedItems);
  };

  const updateSetNotSavedTab = (
    updatedItems: CreateUpdateContractEstimateItemDto[],
  ) => {
    const dataChanged = !isEqual(
      {
        ...(props.extendedContract?.contractEstimateItems?.map(o =>
          convert(o),
        ) || []),
      },
      { ...updatedItems },
    );
    props.setNotSavedTab(
      dataChanged ? ContractTabsEnum.ESTIMATE_ITEM_POSITIONS : undefined,
    );
  };

  const handleFillAllAmounts = () => {
    let updatedItems = contractEstimateItems;

    const result = availableEstimateItems?.map(estItem => {
      if (estItem) {
        const amount = mathUtils
          .add(
            getNotAssignedAmount(estItem),
            mathUtils.round(
              props.extendedContract?.contractEstimateItems?.find(
                p => p.estimateItem?.id === estItem.id,
              )?.amount || 0,
            ),
          )
          .toString();
        const newContractEstimateItem = getContractItem(
          estItem.id,
          contractEstimateItems,
        );

        updatedItems = handleAmountChangeWithoutChangeState(
          estItem.id,
          newContractEstimateItem,
          amount,
          updatedItems,
          props.contractId,
        );

        setContractEstimateItems(updatedItems);
        updateSetNotSavedTab(updatedItems);
        return {
          ...estItem,
          amount: amount,
        };
      }

      return estItem;
    });

    setAvailableEstimateItems(result);
  };

  const contractNotRequiredPositions = [
    ...new Set(
      (allAvailableEstimateItems || [])
        .filter(
          o =>
            o.position?.contractNotRequired &&
            props.extendedContract?.contractEstimateItems?.some(
              p => p.estimateItem?.id === o.id,
            ),
        )
        .map(o => o.position.name),
    ),
  ];

  const handleChangeFilters = (fil: Partial<FiltersParams>) => {
    setFilters({ ...filters, ...fil });
  };

  return (
    <>
      <ContractEstimateItemFilters
        filters={filters}
        handleChangeFilters={handleChangeFilters}
      />
      <ContractEstimateItemGrid
        extendedContract={props.extendedContract}
        contractEstimateItems={contractEstimateItems}
        availableEstimateItems={availableEstimateItems || []}
        readOnly={
          props.readonly ||
          (props.contract?.status !== ContractStatusEnum.Entered &&
            props.contract?.status !== ContractStatusEnum.Unsigned)
        }
        handleRefresh={props.handleRefresh}
        handleAmountChange={handleAmountChange}
        warnings={[]}
        handleFillAllAmounts={handleFillAllAmounts}
        sumData={sumData}
      />
      {!!contractNotRequiredPositions.length && (
        <Alert variant="danger" className="m-0 mt-4">
          {t(tk.contract.contractHasPositionContractNotRequired, {
            positions: contractNotRequiredPositions.join(', '),
          })}
        </Alert>
      )}
      <ValidationAlert
        show={!!saveErrors.length}
        errors={saveErrors}
        className="m-0 mt-4"
      />
      <ContractEstimateItemControls
        {...props}
        handleRefresh={props.handleRefresh}
        readOnly={
          props.readonly ||
          (props.contract?.status !== ContractStatusEnum.Entered &&
            props.contract?.status !== ContractStatusEnum.Unsigned)
        }
        contractEstimateItems={contractEstimateItems}
        warnings={saveErrors}
        setNotSavedTab={props.setNotSavedTab}
        notSavedTab={props.notSavedTab}
        setSaveErrors={setSaveErrors}
        allAvailableEstimateItems={availableEstimateItems || []}
        signingBlocked={!!contractNotRequiredPositions.length}
      />
    </>
  );
};
